import { t } from "i18next";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { Season } from "../../../../interfaces/season";
import { getSeasonOptions } from "../../../../services/season";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { getTeamOptions } from "../../../../services/team";
import { Team } from "../../../../interfaces/team";
import { getPlanningReports, getCoachPlanningMonitoringList, getCoachPlanningMonitoring } from "../../../../services/planning";
import { emptyPlanningReporting, PlanningReporting, PlanningReportingDay, PlanningReportingWeek, PlanningReportingDayMoment, PlanningReportingMonth, emptyPlanningReportingDayMoment, PlanningReportingDaySession, emptyPlanningReportingDaySession} from "../../../../interfaces/planning";
import { Club } from "../../../../interfaces/club";
import { getClubOptions } from "../../../../services/club";
import { BarsChart } from "../../../charts/bars-chart";
import moment from 'moment';
import { CoachPlanningMomentSessionDetails } from "./CoachPlanningMomentSessionDetails";

function CoachPlanningReports() {
    const [loaded, setLoaded] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [planning, setPlanning] = useState<PlanningReporting>(emptyPlanningReporting);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedWeek, setSelectedWeek] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    const [selectedMomentSession, setSelectedMomentSession] = useState<PlanningReportingDaySession>(emptyPlanningReportingDaySession);
    const [expandedMonths, setExpandedMonths] = useState<string[]>([]);
    const [expandedWeeks, setExpandedWeeks] = useState<string[]>([]);
    const [avgFatigueLevel, setAvgFatigueLevel] = useState(0);
    const [avgDuration, setAvgDuration] = useState(0);
    const [avgTemperature, setAvgTemperature] = useState(0);
    const [avgSpeed, setAvgSpeed] = useState(0);
    const [avgWindSpeed, setAvgWindSpeed] = useState(0);
    const [chartCategories, setChartCategories] = useState<string[]>([]);
    const [avgFatigueLevelData, setAvgFatigueLevelData] = useState<number[]>([]);
    const [avgDurationData, setAvgDurationData] = useState<number[]>([]);
    const [avgTemperatureData, setAvgTemperatureData] = useState<number[]>([]);
    const [avgSpeedData, setAvgSpeedData] = useState<number[]>([]);
    const [avgWindSpeedData, setAvgWindSpeedData] = useState<number[]>([]);
    const [momentList, setMomentList] = useState<PlanningReportingDayMoment[]>([]);
    const [monitoringListBySession, setMonitoringListBySession] = useState<{[key: string]: any[]}>({});
    const [showMomentSessionDetails, setShowMomentSessionDetails] = useState(false);

    const [seasonOptions, setSeasonOptions] = useState<Season[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState(0);
    const [selectedSeasonName, setSelectedSeasonName] = useState('');
    const [clubOptions, setClubOptions] = useState<Club[]>([]);
    const [selectedClubId, setSelectedClubId] = useState(0);
    const [selectedClubName, setSelectedClubName] = useState('');
    const [teamOptions, setTeamOptions] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState(0);
    const [selectedTeamName, setSelectedTeamName] = useState('');

    useEffect(() => {
        Promise.all([
            getSeasonOptions()
        ]).then((response: any) => {
            setSeasonOptions(response[0].data);
            setLoaded(true);
        })
        .catch((error: any) => {
            console.error('Error loading season options:', error);
            setLoaded(true);
            alert(t("Error loading season options. Please refresh the page."));
        });
    }, []);
    
    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const seasonIndex = seasonOptions.findIndex((s: Season) => s.id == event.target.season.value);
        if (seasonIndex === -1) {
            setSelectedSeasonId(0);
            setSelectedSeasonName('');
        } else {
            setSelectedSeasonId(seasonOptions[seasonIndex].id);
            setSelectedSeasonName(seasonOptions[seasonIndex].name);
        }

        const clubIndex = clubOptions.findIndex((t: Club) => t.id == event.target.club.value);
        if (clubIndex === -1) {
            setSelectedClubId(0);
            setSelectedClubName('');
        } else {
            setSelectedClubId(clubOptions[clubIndex].id);
            setSelectedClubName(clubOptions[clubIndex].name);
        }

        const teamIndex = teamOptions.findIndex((t: Team) => t.id == event.target.team.value);
        if (teamIndex === -1) {
            setSelectedTeamId(0);
            setSelectedTeamName('');
        } else {
            setSelectedTeamId(teamOptions[teamIndex].id);
            setSelectedTeamName(teamOptions[teamIndex].name);
        }

        setPlanning(emptyPlanningReporting);
        setRefreshing(true);

        const season = event.target.season.value;
        const team = event.target.team.value;

        getPlanningReports(season, team)
            .then((response: any) => {
                setPlanning(response.data);
                if (response.data.has_data == 1) {
                    setAvgFatigueLevel(response.data.avg_fatigue_level);
                    setAvgDuration(response.data.avg_duration);
                    setAvgTemperature(response.data.avg_temperature);
                    setAvgSpeed(response.data.avg_speed_avg);
                    setAvgWindSpeed(response.data.avg_wind_speed);
                    setChartCategories(response.data.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) => {
                        return moment(m.month).format('MMM Y');
                    }));
                    setAvgFatigueLevelData(response.data.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                        return m.avg_fatigue_level;
                    }));
                    setAvgDurationData(response.data.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                        return m.avg_duration;
                    }));
                    setAvgTemperatureData(response.data.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                        return m.avg_temperature;
                    }));
                    setAvgSpeedData(response.data.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                        return m.avg_speed_avg;
                    }));
                    setAvgWindSpeedData(response.data.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                        return m.avg_wind_speed;
                    }));
                }
                setRefreshing(false);
            })
            .catch((error: any) => {
                console.error('Error loading planning reports:', error);
                setRefreshing(false);
                alert(t("Error loading planning reports. Please try again."));
            });
    }

    const handleSeasonChange = (event: any) => {
        const currentSeason = event.currentTarget.value;
        setSelectedSeasonId(currentSeason);
        setSelectedClubId(0);
        setSelectedTeamId(0);
        setClubOptions([]);
        setTeamOptions([]);
        setPlanning(emptyPlanningReporting);
        if (currentSeason != 0) {
            getClubOptions('', currentSeason)
                .then((response: any) => {
                    setClubOptions(response.data);
                });
        }
    }

    const handleClubChange = (event: any) => {
        const currentClub = event.currentTarget.value;
        setSelectedClubId(currentClub);
        setSelectedTeamId(0);
        setTeamOptions([]);
        setPlanning(emptyPlanningReporting);
        if (currentClub != 0) {
            getTeamOptions(selectedSeasonId, currentClub)
                .then((response: any) => {
                    setTeamOptions(response.data);
                });
        }
    }

    const handleTeamChange = (event: any) => {
        const currentTeam = event.currentTarget.value;
        setSelectedTeamId(currentTeam);
        setPlanning(emptyPlanningReporting);
    }

    const handleSelectTreeItem = (event: any) => {
        const month = event.currentTarget.dataset.month;
        setSelectedMonth(month);
        const week = event.currentTarget.dataset.week;
        setSelectedWeek(week);
        const day = event.currentTarget.dataset.day;
        setSelectedDay(day);

        setAvgFatigueLevel(0);
        setAvgDuration(0);
        setAvgTemperature(0);
        setAvgSpeed(0);
        setAvgWindSpeed(0);
        setChartCategories([]);
        setAvgFatigueLevelData([]);
        setAvgDurationData([]);
        setAvgTemperatureData([]);
        setAvgSpeedData([]);
        setAvgWindSpeedData([]);
        setMomentList([]);
        
        if (month == '' && week == '' && day == '') {
            if (planning.has_data == 1) {
                setAvgFatigueLevel(planning.avg_fatigue_level);
                setAvgDuration(planning.avg_duration);
                setAvgTemperature(planning.avg_temperature);
                setAvgSpeed(planning.avg_speed_avg);
                setAvgWindSpeed(planning.avg_wind_speed);
                setChartCategories(planning.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) => {
                    return moment(m.month).format('MMM Y');
                }));
                setAvgFatigueLevelData(planning.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                    return m.avg_fatigue_level;
                }));
                setAvgDurationData(planning.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                    return m.avg_duration;
                }));
                setAvgTemperatureData(planning.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                    return m.avg_temperature;
                }));
                setAvgSpeedData(planning.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                    return m.avg_speed_avg;
                }));
                setAvgWindSpeedData(planning.months.filter((mf: PlanningReportingMonth) => mf.has_data == 1).map((m: PlanningReportingMonth) =>{
                    return m.avg_wind_speed;
                }));
            }
        }
        if (month != '' && week == '' && day == '') {
            const index = planning.months.findIndex((m: PlanningReportingMonth) => m.month == month);
            if (index !== -1) {
                if (planning.months[index].has_data == 1) {
                    setAvgFatigueLevel(planning.months[index].avg_fatigue_level);
                    setAvgDuration(planning.months[index].avg_duration);
                    setAvgTemperature(planning.months[index].avg_temperature);
                    setAvgSpeed(planning.months[index].avg_speed_avg);
                    setAvgWindSpeed(planning.months[index].avg_wind_speed);
                    setChartCategories(planning.months[index].weeks.filter((wf: PlanningReportingWeek) => wf.has_data == 1).map((w: PlanningReportingWeek) => {
                        return w.week;
                    }));
                    setAvgFatigueLevelData(planning.months[index].weeks.filter((wf: PlanningReportingWeek) => wf.has_data == 1).map((w: PlanningReportingWeek) =>{
                        return w.avg_fatigue_level;
                    }));
                    setAvgDurationData(planning.months[index].weeks.filter((wf: PlanningReportingWeek) => wf.has_data == 1).map((w: PlanningReportingWeek) =>{
                        return w.avg_duration;
                    }));
                    setAvgTemperatureData(planning.months[index].weeks.filter((wf: PlanningReportingWeek) => wf.has_data == 1).map((w: PlanningReportingWeek) =>{
                        return w.avg_temperature;
                    }));
                    setAvgSpeedData(planning.months[index].weeks.filter((wf: PlanningReportingWeek) => wf.has_data == 1).map((w: PlanningReportingWeek) =>{
                        return w.avg_speed_avg;
                    }));
                    setAvgWindSpeedData(planning.months[index].weeks.filter((wf: PlanningReportingWeek) => wf.has_data == 1).map((w: PlanningReportingWeek) =>{
                        return w.avg_wind_speed;
                    }));
                }
            }
        } 
        if (month != '' && week != '' && day == '') {
            const monthIndex = planning.months.findIndex((m: PlanningReportingMonth) => m.month == month);
            if (monthIndex !== -1) {
                const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningReportingWeek) => w.week == week);
                if (weekIndex !== -1) {
                    if (planning.months[monthIndex].weeks[weekIndex].has_data == 1) {
                        setAvgFatigueLevel(planning.months[monthIndex].weeks[weekIndex].avg_fatigue_level);
                        setAvgDuration(planning.months[monthIndex].weeks[weekIndex].avg_duration);
                        setAvgTemperature(planning.months[monthIndex].weeks[weekIndex].avg_temperature);
                        setAvgSpeed(planning.months[monthIndex].weeks[weekIndex].avg_speed_avg);
                        setAvgWindSpeed(planning.months[monthIndex].weeks[weekIndex].avg_wind_speed);
                        setChartCategories(planning.months[monthIndex].weeks[weekIndex].days.filter((df: PlanningReportingDay) => df.has_data == 1).map((d: PlanningReportingDay) => {
                            return d.day;
                        }));
                        setAvgFatigueLevelData(planning.months[monthIndex].weeks[weekIndex].days.filter((df: PlanningReportingDay) => df.has_data == 1).map((d: PlanningReportingDay) =>{
                            return d.avg_fatigue_level;
                        }));
                        setAvgDurationData(planning.months[monthIndex].weeks[weekIndex].days.filter((df: PlanningReportingDay) => df.has_data == 1).map((d: PlanningReportingDay) =>{
                            return d.avg_duration;
                        }));
                        setAvgTemperatureData(planning.months[monthIndex].weeks[weekIndex].days.filter((df: PlanningReportingDay) => df.has_data == 1).map((d: PlanningReportingDay) =>{
                            return d.avg_temperature;
                        }));
                        setAvgSpeedData(planning.months[monthIndex].weeks[weekIndex].days.filter((df: PlanningReportingDay) => df.has_data == 1).map((d: PlanningReportingDay) =>{
                            return d.avg_speed_avg;
                        }));
                        setAvgWindSpeedData(planning.months[monthIndex].weeks[weekIndex].days.filter((df: PlanningReportingDay) => df.has_data == 1).map((d: PlanningReportingDay) =>{
                            return d.avg_wind_speed;
                        }));
                    }
                }
            }
        } 
        if (month != '' && week != '' && day != '') {
            const monthIndex = planning.months.findIndex((m: PlanningReportingMonth) => m.month == month);
            if (monthIndex !== -1) {
                const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningReportingWeek) => w.week == week);
                if (weekIndex !== -1) {
                    const dayIndex = planning.months[monthIndex].weeks[weekIndex].days.findIndex((d: PlanningReportingDay) => d.day == day);
                    if (dayIndex !== -1) {
                        if (planning.months[monthIndex].weeks[weekIndex].days[dayIndex].has_data == 1) {
                            setAvgFatigueLevel(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].avg_fatigue_level);
                            setAvgDuration(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].avg_duration);
                            setAvgTemperature(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].avg_temperature);
                            setAvgSpeed(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].avg_speed_avg);
                            setAvgWindSpeed(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].avg_wind_speed);
                            setChartCategories(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => mf.has_data == 1).map((m: PlanningReportingDayMoment) => {
                                return t(m.day_moment);
                            }));

                            setAvgFatigueLevelData(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => mf.has_data == 1).map((m: PlanningReportingDayMoment) =>{
                                return m.avg_fatigue_level;
                            }));
                            setAvgDurationData(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => mf.has_data == 1).map((m: PlanningReportingDayMoment) =>{
                                return m.avg_duration;
                            }));
                            setAvgTemperatureData(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => mf.has_data == 1).map((m: PlanningReportingDayMoment) =>{
                                return m.avg_temperature;
                            }));
                            setAvgSpeedData(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => mf.has_data == 1).map((m: PlanningReportingDayMoment) =>{
                                return m.avg_speed_avg;
                            }));
                            setAvgWindSpeedData(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => mf.has_data == 1).map((m: PlanningReportingDayMoment) =>{
                                return m.avg_wind_speed;
                            }));

                            const filteredMoments = planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.filter((mf: PlanningReportingDayMoment) => {
                                // Considerar has_data == 1 o si tiene sesiones con datos
                                return mf.has_data == 1 || mf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1);
                            });

                            setMomentList(filteredMoments);

                            // Cargar monitorizaciones para cada sesión
                            const monitoringPromises: Promise<any>[] = [];
                            filteredMoments.forEach((m: PlanningReportingDayMoment) => {
                                m.day_moment_session.forEach((s: PlanningReportingDaySession) => {
                                    if (s.has_data == 1) {
                                        const promise = getCoachPlanningMonitoringList(
                                            selectedTeamId,
                                            day,
                                            m.day_moment,
                                            s.day_moment_session
                                        ).then(response => {
                                            return {
                                                key: `${m.day_moment}-${s.day_moment_session}`,
                                                data: response.data || []
                                            };
                                        }).catch(() => {
                                            return {
                                                key: `${m.day_moment}-${s.day_moment_session}`,
                                                data: []
                                            };
                                        });
                                        monitoringPromises.push(promise);
                                    }
                                });
                            });

                            Promise.all(monitoringPromises).then(results => {
                                const monitoringsBySession: {[key: string]: any[]} = {};
                                results.forEach(result => {
                                    monitoringsBySession[result.key] = result.data;
                                });
                                setMonitoringListBySession(monitoringsBySession);
                            });
                        }
                    }
                }
            }
        } 
    }

    const handleShowMomentDetails = (event: any) => {
        event.preventDefault();
        const moment = event.currentTarget.dataset.moment;
        const session = event.currentTarget.dataset.session;
        const monitoringId = event.currentTarget.dataset.monitoringid;

        if (monitoringId) {
            // Cargar monitorización específica
            getCoachPlanningMonitoring(selectedTeamId, selectedDay, moment, session, parseInt(monitoringId))
                .then(response => {
                    const data = response.data;
                    if (data && data.events) {
                        // Crear un objeto de sesión con los datos de la monitorización
                        const sessionData: PlanningReportingDaySession = {
                            day_moment_session: session,
                            avg_duration: 0,
                            avg_fatigue_level: 0,
                            avg_speed_avg: 0,
                            avg_temperature: 0,
                            avg_wind_speed: 0,
                            has_data: 1,
                            athletes: [],
                            monitoring: data.events || [],
                            monitoring_meta: data.meta || null,
                            monitoring_athletes: data.athletes || []
                        };
                        setSelectedMomentSession(sessionData);
                        setShowMomentSessionDetails(true);
                    }
                })
                .catch(error => {
                    console.error('Error loading monitoring details:', error);
                });
        } else {
            // Comportamiento original para monitorizaciones antiguas
            const monthIndex = planning.months.findIndex((m: PlanningReportingMonth) => m.month == selectedMonth);
            if (monthIndex !== -1) {
                const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningReportingWeek) => w.week == selectedWeek);
                if (weekIndex !== -1) {
                    const dayIndex = planning.months[monthIndex].weeks[weekIndex].days.findIndex((d: PlanningReportingDay) => d.day == selectedDay);
                    if (dayIndex !== -1) {
                        if (planning.months[monthIndex].weeks[weekIndex].days[dayIndex].has_data == 1) {
                            const momentIndex = planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment.findIndex((m: PlanningReportingDayMoment) => m.day_moment == moment);
                            if (momentIndex !== -1) {
                                if (planning.months[monthIndex].weeks[weekIndex].days[dayIndex].has_data == 1) {
                                    const sessionIndex = planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment[momentIndex].day_moment_session.findIndex((s: PlanningReportingDaySession) => s.day_moment_session == session);
                                    if (sessionIndex !== -1) {
                                        setSelectedMomentSession(planning.months[monthIndex].weeks[weekIndex].days[dayIndex].day_moment[momentIndex].day_moment_session[sessionIndex]);
                                        setShowMomentSessionDetails(true);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    const handleMomentSessionDetailsClose = () => {
        setShowMomentSessionDetails(false);
    }

    const toggleMonth = (month: string) => {
        if (expandedMonths.includes(month)) {
            setExpandedMonths(expandedMonths.filter(m => m !== month));
        } else {
            setExpandedMonths([...expandedMonths, month]);
        }
    }

    const toggleWeek = (week: string) => {
        if (expandedWeeks.includes(week)) {
            setExpandedWeeks(expandedWeeks.filter(w => w !== week));
        } else {
            setExpandedWeeks([...expandedWeeks, week]);
        }
    }

    return (
        <div className="reports-container">
            <h1>
                { t("Planning reports")}
            </h1>
            <div className="filters-container">
                { !loaded &&
                    <LoadingSpinner />
                }
                { loaded &&
                    <Form className="form-planning-filter" onSubmit={ handleSearchSubmit }>
                        <Form.Group className="field-season" controlId="season">
                            <Form.Select aria-label={ t("season") } name="season_id" required onChange={ handleSeasonChange }>
                                <option value="">{ t("select_season") }</option>
                                { seasonOptions.map((s: Season) => {
                                    return <option key={ 'season-' + s.id }
                                        value={ s.id }>{ s.name }</option>
                                })}
                            </Form.Select>
                        </Form.Group>
                        { selectedSeasonId != 0 &&
                            <Form.Group className="field-club" controlId="club">
                                <Form.Select aria-label={ t("club") } name="club_id" required onChange={ handleClubChange }>
                                    <option value="">{ t("select_club") }</option>
                                    { clubOptions.map((t: Club) => {
                                        return <option key={ 'club-' + t.id }
                                            value={ t.id }>{ t.name }</option>
                                    })}
                                </Form.Select>
                            </Form.Group>
                        }
                        { selectedClubId != 0 &&
                            <Form.Group className="field-team" controlId="team">
                                <Form.Select aria-label={ t("team") } name="team_id" required onChange={ handleTeamChange }>
                                    <option value="">{ t("select_team") }</option>
                                    { teamOptions.map((t: Team) => {
                                        return <option key={ 'team-' + t.id }
                                            value={ t.id }>{ t.name }</option>
                                    })}
                                </Form.Select>
                            </Form.Group>
                        }
                        { selectedSeasonId != 0 && selectedClubId != 0 && selectedTeamId != 0 &&
                            <div className="actions">
                                <Button variant="primary" type="submit">
                                    { t("search") }
                                </Button>
                            </div>
                        }
                    </Form>
                }
            </div>
            { loaded && refreshing && <LoadingSpinner /> }
            { loaded && !refreshing && planning.months.length > 0 &&
                <div className="reports-content">
                    <Row>
                        <Col xs={ 12 } lg={ 4 } xl={ 3 }>
                            <div className="timeline">
                                <h2>{ 'Timeline' }</h2>
                                { planning.months.length == 0 && 
                                    <Alert key={'info'} variant={'info'}>
                                        { t("There is no reports data") }
                                    </Alert>
                                }
                                { planning.months.length > 0 && 
                                    <div className="timeline-items">
                                        <div className={ 'activity-all' + ((selectedMonth == '' && selectedWeek == '' && selectedDay == '') ? ' active' : '') } key={ 'a-all' }>
                                            <button className="btn-unformatted" data-month={ '' } data-week={ '' } data-day={ '' } onClick={ handleSelectTreeItem }>
                                                { t("All season") }
                                            </button>
                                        </div>
                                        { planning.months.map((m: PlanningReportingMonth) => {
                                            return <div className={ 'planning-month' + (selectedMonth == m.month ? ' active' : '') } key={ 'month-' + m.month }>
                                                <div className="month-header">
                                                    <button className="btn-toggle" onClick={() => toggleMonth(m.month)}>
                                                        <i className={`bi bi-chevron-${expandedMonths.includes(m.month) ? 'down' : 'right'}`}></i>
                                                    </button>
                                                    { m.has_data == 1 &&
                                                        <button className="btn-unformatted" data-month={ m.month } data-week={ '' } data-day={ '' } onClick={ handleSelectTreeItem }>
                                                            { moment(m.month).format('MMM Y') }
                                                            { m.weeks.reduce((total: number, w: PlanningReportingWeek) => total + w.days.reduce((dayTotal: number, d: PlanningReportingDay) => dayTotal + d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length, 0), 0) > 0 &&
                                                                <span className="item-count">({ m.weeks.reduce((total: number, w: PlanningReportingWeek) => total + w.days.reduce((dayTotal: number, d: PlanningReportingDay) => dayTotal + d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length, 0), 0) })</span>
                                                            }
                                                        </button>
                                                    }
                                                    { m.has_data == 0 &&
                                                        <div>
                                                            { moment(m.month).format('MMM Y') }
                                                        </div>
                                                    }
                                                </div>
                                                { expandedMonths.includes(m.month) && <div className="month-weeks">
                                                    { m.weeks.map((w: PlanningReportingWeek) => {
                                                        return (<div className={ 'planning-week' + (selectedWeek == w.week ? ' active' : '') } key={ 'week-' + w.week }>
                                                            <div className="week-header">
                                                                <button className="btn-toggle" onClick={() => toggleWeek(w.week)}>
                                                                    <i className={`bi bi-chevron-${expandedWeeks.includes(w.week) ? 'down' : 'right'}`}></i>
                                                                </button>
                                                                { w.has_data == 1 &&
                                                                    <button className="btn-unformatted" data-month={ m.month } data-week={ w.week } data-day={ '' }
                                                                        title={ t("Week") + ' ' + w.week } onClick={ handleSelectTreeItem }>
                                                                        { t("Week") + ' ' + w.week_num }
                                                                        { w.days.reduce((dayTotal: number, d: PlanningReportingDay) => dayTotal + d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length, 0) > 0 &&
                                                                            <span className="item-count">({ w.days.reduce((dayTotal: number, d: PlanningReportingDay) => dayTotal + d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length, 0) })</span>
                                                                        }
                                                                    </button>
                                                                }
                                                                { w.has_data == 0 &&
                                                                    <div>
                                                                        { t("Week") + ' ' + w.week_num }
                                                                    </div>
                                                                }
                                                            </div>
                                                            { expandedWeeks.includes(w.week) && <div className="month-days">
                                                                { w.days.map((d: PlanningReportingDay) => {
                                                                    return (<div className={ 'planning-day' + (selectedDay == d.day ? ' active' : '') } key={ 'd-' + d.day }>
                                                                        { d.has_data == 1 &&
                                                                            <button className="btn-unformatted" data-month={ m.month } data-week={ w.week } data-day={ d.day } onClick={ handleSelectTreeItem } title={ d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length + ' ' + t("moment-item") }>
                                                                                { moment(d.day).format('D') }
                                                                                { d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length > 0 &&
                                                                                    <span className="day-count">{ d.day_moment.filter((wf: PlanningReportingDayMoment) => wf.has_data == 1 || wf.day_moment_session.some((s: PlanningReportingDaySession) => s.has_data == 1)).length }</span>
                                                                                }
                                                                            </button>
                                                                        }
                                                                        { d.has_data == 0 &&
                                                                            <div className="no-moments">
                                                                                { moment(d.day).format('D') }
                                                                            </div>
                                                                        }
                                                                    </div>)
                                                                })}
                                                            </div> }
                                                        </div>)
                                                    })}
                                                </div> }
                                            </div>;
                                        }) }
                                    </div>
                                }
                            </div>
                        </Col>    
                        <Col xs={ 12 } lg={ 8 } xl={ 9 }>
                            { planning.months.length > 0 && 
                                <div className="report-data-content">
                                    <h2>
                                        { t("Aggregated data for") }
                                        { selectedMonth == '' && <strong>&nbsp;{ t("All season") }</strong> }
                                        { selectedMonth != '' && selectedWeek == '' && <strong>&nbsp;{ moment(selectedMonth).format('MMM Y') }</strong> }
                                        { selectedMonth != '' && selectedWeek != '' && selectedDay == '' && <strong>&nbsp;{ moment(selectedMonth).format('MMM') + ' ' + selectedWeek }</strong> }
                                        { selectedMonth != '' && selectedWeek != '' && selectedDay != '' && <strong>&nbsp;{ moment(selectedDay).format('D/M/Y   ') }</strong> }
                                    </h2>
                                    <div className="summary">
                                        <div className="totals">
                                            <div className="fatigue-level report-kpi">
                                                <div className="value">
                                                    { (avgFatigueLevel || 0).toFixed(2) }
                                                </div>
                                                <div className="label">
                                                    { t("Fatigue level Avg") }
                                                </div>
                                            </div>
                                            <div className="duration report-kpi">
                                                <div className="value">
                                                    { moment.utc((avgDuration || 0)*3600*1000).format('HH:mm:ss') }
                                                </div>
                                                <div className="label">
                                                    { t("Duration Avg") }
                                                </div>
                                            </div>
                                            <div className="temperature report-kpi">
                                                <div className="value">
                                                    { (avgTemperature || 0).toFixed(2) } <span className="units">{ t("ºC") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Temperature Avg") }
                                                </div>
                                            </div>
                                            <div className="speed report-kpi">
                                                <div className="value">
                                                    { (avgSpeed || 0).toFixed(2) } <span className="units">{ t("k/h") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Speed Avg") }
                                                </div>
                                            </div>
                                            <div className="wind report-kpi">
                                                <div className="value">
                                                    { (avgWindSpeed || 0).toFixed(2) } <span className="units">{ t("k/h") }</span>
                                                </div>
                                                <div className="label">
                                                    { t("Wind Speed Avg") }
                                                </div>
                                            </div>
                                        </div>
                                        { momentList.length > 0 &&
                                            <div className="moment-list">
                                                <h4>{ t("Monitoring") }</h4>
                                                <ol className="list">
                                                    {
                                                        momentList.map((m: PlanningReportingDayMoment) => {
                                                            return m.day_moment_session.map((s: PlanningReportingDaySession) => {
                                                                const sessionKey = `${m.day_moment}-${s.day_moment_session}`;
                                                                const monitorings = monitoringListBySession[sessionKey] || [];

                                                                if (monitorings.length > 0) {
                                                                    // Mostrar cada monitorización como un item separado
                                                                    return monitorings.map((monitoring: any) => {
                                                                        const durationStr = monitoring.start_time && monitoring.finish_time ? (() => {
                                                                            const duration = moment.duration(moment(monitoring.finish_time).diff(moment(monitoring.start_time)));
                                                                            const hours = Math.floor(duration.asHours());
                                                                            const minutes = duration.minutes();
                                                                            const seconds = duration.seconds();
                                                                            return hours > 0
                                                                                ? `${hours}h ${minutes}m ${seconds}s`
                                                                                : minutes > 0
                                                                                    ? `${minutes}m ${seconds}s`
                                                                                    : `${seconds}s`;
                                                                        })() : '-';

                                                                        const athleteNames = monitoring.athletes
                                                                            .map((a: any) => a.athlete_name || a.name)
                                                                            .filter((name: string) => name)
                                                                            .sort((a: string, b: string) => a.localeCompare(b))
                                                                            .join(', ');

                                                                        return (
                                                                            <li className="session-item" key={`session-item-${monitoring.monitoring_id}`}>
                                                                                <button
                                                                                    className="btn-unformatted"
                                                                                    data-moment={m.day_moment}
                                                                                    data-session={s.day_moment_session}
                                                                                    data-monitoringid={monitoring.monitoring_id}
                                                                                    onClick={handleShowMomentDetails}
                                                                                >
                                                                                    <span className="moment-label">{t(m.day_moment)}</span>: {t("Session")} {s.day_moment_session} -
                                                                                    <span style={{marginLeft: '0.5rem'}}>
                                                                                        <i className="bi bi-people"></i> {monitoring.athletes.length}
                                                                                        {monitoring.start_time && <> | <i className="bi bi-clock"></i> {moment(monitoring.start_time).format('HH:mm')}</>}
                                                                                        {durationStr !== '-' && <> | <i className="bi bi-hourglass-split"></i> {durationStr}</>}
                                                                                        {monitoring.lap_count > 0 && <> | {monitoring.lap_count} {t("laps")}</>}
                                                                                        {monitoring.avg_shovelfuls > 0 && (
                                                                                            <> | <i className="bi bi-activity"></i> {parseFloat(monitoring.avg_shovelfuls).toFixed(1)}</>
                                                                                        )}
                                                                                    </span>
                                                                                </button>
                                                                                {athleteNames && (
                                                                                    <div className="athlete-names text-muted" style={{fontSize: '0.85em', marginLeft: '1rem'}}>
                                                                                        <i className="bi bi-people"></i> {athleteNames}
                                                                                    </div>
                                                                                )}
                                                                            </li>
                                                                        );
                                                                    });
                                                                } else {
                                                                    // Fallback: mostrar sesión sin monitorizaciones
                                                                    return (
                                                                        <li className="session-item" key={'session-item-' + m.day_moment + '-' + s.day_moment_session}>
                                                                            <button
                                                                                className="btn-unformatted"
                                                                                data-moment={m.day_moment}
                                                                                data-session={s.day_moment_session}
                                                                                onClick={handleShowMomentDetails}
                                                                            >
                                                                                <span className="moment-label">{t(m.day_moment)}</span>: {t("Session")} {s.day_moment_session}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                }
                                                            });
                                                        })
                                                    }
                                                </ol>
                                            </div>
                                        }
                                    </div>
                                    { chartCategories.length > 0 && avgFatigueLevelData.length > 0 && 
                                        <BarsChart title={ t("Fatigue level") } 
                                            category={ chartCategories }
                                            data={ avgFatigueLevelData }
                                            units={ '' }
                                            color={ '#34B689' }
                                            yAxisType="value" />
                                    }
                                    { chartCategories.length > 0 && avgDurationData.length > 0 && 
                                        <BarsChart title={ t("Duration") } 
                                            category={ chartCategories }
                                            data={ avgDurationData }
                                            units={ '' }
                                            color={ '#F92C84' }
                                            yAxisType="time" />
                                    }
                                    { chartCategories.length > 0 && avgTemperatureData.length > 0 && 
                                        <BarsChart title={ t("Temperature") } 
                                            category={ chartCategories }
                                            data={ avgTemperatureData }
                                            units={ t("ºC") }
                                            color={ '#FABF48' }
                                            yAxisType="value" />
                                    }
                                    { chartCategories.length > 0 && avgSpeedData.length > 0 && 
                                        <BarsChart title={ t("Speed") } 
                                            category={ chartCategories }
                                            data={ avgSpeedData }
                                            units={ t("k/h") }
                                            color={ '#009FE3' }
                                            yAxisType="value" />
                                    }
                                    { chartCategories.length > 0 && avgWindSpeedData.length > 0 && 
                                        <BarsChart title={ t("Wind Speed") } 
                                            category={ chartCategories }
                                            data={ avgWindSpeedData }
                                            units={ t("k/h") }
                                            color={ '#e300c1ff' }
                                            yAxisType="value" />
                                    }
                                </div>
                            }
                        </Col>                        
                    </Row>
                </div>
            }
            { showMomentSessionDetails &&
                <CoachPlanningMomentSessionDetails session={ selectedMomentSession } close={ handleMomentSessionDetailsClose } />
            }
        </div>
    );
}

export default CoachPlanningReports;